import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { NavLink } from 'react-router-dom';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import classes from './chat.module.scss';
import {
  getMessagesRequest,
  sendMessageRequest,
  getChatUserListRequest,
  scrollToTop,
} from '../../../store/actions/rightBar';
import UserTabs from './UserTab/UserTab';
import accountHelpers from '../../../utils/accountHelpers';
import utils from '../../../utils/utils';
import Loader from '../../_common/Loader/Loader';
import avatarImg from '../../../assets/icons/avatar.png';
import api from '../../../api/api';
import { ReactComponent as Bell } from '../../../assets/icons/bell.svg';

const userRolesTemplate = [
  {
    tabName: 'SortPak',
    requestRoles: 'Pharmacy,Admin',
    showForRoles: ['Admin', 'Pharmacy', 'Rep', 'Physician', 'Patient'],
    color: '#FF0000',
  },
  {
    tabName: 'Sales Rep',
    requestRoles: 'Rep',
    showForRoles: ['Admin', 'Pharmacy', 'Rep', 'Physician'],
    color: '#547EEE',
  },
  {
    tabName: 'Partner',
    requestRoles: 'Physician',
    showForRoles: ['Admin', 'Pharmacy', 'Rep'],
    color: '#00C974',
  },
  {
    tabName: 'Members',
    requestRoles: 'Patient',
    showForRoles: ['Admin', 'Pharmacy', 'Rep', 'Physician', 'Health'],
    color: '#5F6C8E',
  },
];

const focusRange = (el, lastPosition) => {
  const range = document.createRange();
  const sel = window.getSelection();
  range.setStart(el.childNodes[1], lastPosition || el.childNodes[1].length || 1);
  range.collapse(true);

  sel.removeAllRanges();
  sel.addRange(range);
};

const limit = 20;

let firstLoad = true;
let timeout = true;

const Chat = () => {
  const textArea = useRef(null);
  const userWrapperRef = useRef(null);
  const chatWrapperRef = useRef(null);

  const dispatch = useDispatch();
  const {
    chatUserList, chatUserPage, chatUserTotalPage, messagesList, isScrollToTop,
    chatTotalPages, chatCurrentPage, notificationList,
  } = useSelector((store) => store.rightBar);

  const [user, setUser] = useState({
    nickName: '',
    id: '',
  });
  const [searchUser, setSearchUser] = useState([]);

  const [userRoleTabName, setUserRoleTabName] = useState('SortPak');
  const [chatRequestLoading, chatRequestLoadingToggle] = useState(false);
  const [userRequestLoading, userRequestLoadingToggle] = useState(false);
  const [userLoad, setUserLoad] = useState(false);

  const currentTab = userRolesTemplate.find(({ tabName }) => tabName === userRoleTabName);

  useEffect(() => {
    (async () => {
      firstLoad = true;
      await Promise.all([
        dispatch(getMessagesRequest({ page: 1 })),
        dispatch(getChatUserListRequest({ userRole: 'Pharmacy,Admin', limit })),
      ]);

      firstLoad = false;
    })();

    return () => firstLoad = true;
  }, []);

  useEffect(() => {
    const regex = new RegExp(`^${user.nickName}`);

    const notAllowedNickName = regex.test(textArea.current.innerText)
        && textArea.current.innerText.length > user.nickName.length
      && textArea.current.innerText.slice(user.nickName.length, user.nickName.length + 1) !== ' ';

    if (user.nickName && (notAllowedNickName || !regex.test(textArea.current.innerText))) {
      setUser({
        nickName: '',
        id: '',
      });
    }
  }, [textArea.current && textArea.current.innerHTML]);

  useEffect(() => {
    textArea.current.style.height = '36px';
    textArea.current.style.height = `${Math.max(textArea.current.scrollHeight >= 98
      ? 98
      : textArea.current.scrollHeight + 2, 36)}px`;
  }, [textArea.current && textArea.current.scrollHeight]);

  useEffect(() => {
    if (isScrollToTop) {
      chatWrapperRef.current.scrollTo(0, 0);
      userWrapperRef.current.scrollTo(0, 0);
      dispatch(scrollToTop(false));
    }
  }, [isScrollToTop]);

  const getUsers = async (role, page = 1) => {
    let searchData = { role };

    if (role === 'Rep') {
      searchData.searcherId = accountHelpers.getUser().id;
    } else if (role.includes(',')) {
      searchData = { userRole: role };
    }
    searchData.page = page;

    await dispatch(getChatUserListRequest({ ...searchData, limit }));

    setUserLoad(false);
  }; // tabchange

  const getUsersScroll = async (e) => {
    if (!firstLoad && !userRequestLoading
      && e.target.scrollTop + e.target.getBoundingClientRect().height + 10 >= e.target.scrollHeight) {
      if (chatUserTotalPage >= chatUserPage + 1) {
        userRequestLoadingToggle(true);

        await getUsers(currentTab.requestRoles, chatUserPage + 1);

        userRequestLoadingToggle(false);
      }
    }
  };

  const getChatMsgScroll = async (e) => {
    if (!firstLoad && !chatRequestLoading
      && e.target.scrollTop + e.target.getBoundingClientRect().height + 10 >= e.target.scrollHeight) {
      if (+chatTotalPages >= +chatCurrentPage + 1) {
        chatRequestLoadingToggle(true);

        await dispatch(getMessagesRequest({ page: +chatCurrentPage + 1 }));

        chatRequestLoadingToggle(false);
      }
    }
  };

  const changeTab = (tabName, role) => {
    setUserLoad(true);

    getUsers(role);

    setUserRoleTabName(tabName);
  };

  const changeMessage = ({ target }) => {
    if (/^(@[\w\-@ .]+)/.test(target.innerText) && !/^(@[\w\-@ .]+)\s{2,}/.test(target.innerText)) {
      clearTimeout(timeout);

      timeout = setTimeout(async () => {
        const username = target.innerText.trim().replace(/@/, '');
        const { data: { data } } = await api.getUserList({ username, limit: 100 });

        setSearchUser(data.response);
      }, 400);
    } else {
      clearTimeout(timeout);
      setSearchUser([]);
    }

    const isSpanElement = target.childNodes.length && target.childNodes[0].nodeName === 'SPAN';
    const isSpanLengthMoreNickNameLength = isSpanElement
        && target.childNodes[0].innerText.length > user.nickName.length;

    const nickNameRegex = new RegExp(`^${user.nickName}`);

    if (isSpanLengthMoreNickNameLength && user.nickName && nickNameRegex.test(target.childNodes[0].innerText)) {
      textArea.current.innerHTML = `<span>${user.nickName}</span>${target.innerText.replace(`${user.nickName}`, '')}`;
      focusRange(target, 1);
    }

    textArea.current.style.height = '36px';
    textArea.current.style.height = `${Math.max(target.scrollHeight >= 98 ? 98 : target.scrollHeight + 2, 36)}px`;
  };

  const sendMessage = async (e) => {
    if (e.keyCode === 13) {
      if (!e.shiftKey) {
        e.preventDefault();
        const regex = new RegExp(`^${user.nickName}`);

        const message = e.target.innerText.replace(regex, '');

        if (message.trim()) {
          const sendData = utils.deleteEmptyKeys({
            message,
            mention: user.nickName,
            senderName: accountHelpers.getUser().username,
            createdAt: new Date().toISOString(),
            userId: accountHelpers.getUser().id,
            receiverId: user.id,
            newMessages: true,
          });

          dispatch(sendMessageRequest(sendData));

          textArea.current.style.height = '36px';
          e.target.innerHTML = '';
          clearTimeout(timeout);
          setSearchUser([]);
        }
      }
    }
  };

  const getUser = (userName, userId, type) => {
    if (+userId === +accountHelpers.getUser().id) {
      return;
    }

    const nickNameRegex = new RegExp(`^${user.nickName}`);

    if (type === 'search') {
      setSearchUser([]);

      textArea.current.innerHTML = `<span>@${userName}</span> ${textArea.current.innerText.replace(/^(@[\w\-@ .]+)/, '')}`;
    } else if (!textArea.current.innerHTML) {
      textArea.current.innerHTML = `<span>@${userName}</span> `;
    } else if (user.nickName && nickNameRegex.test(textArea.current.innerText)) {
      textArea.current.innerHTML = `<span>@${userName}</span> ${textArea.current.innerText.replace(`${user.nickName} `, '')}`;
    } else {
      textArea.current.innerHTML = `<span>@${userName}</span> ${textArea.current.innerText}`;
    }

    setUser({
      nickName: `@${userName}`,
      id: userId,
    });

    focusRange(textArea.current);
  };

  const note = notificationList.find((n) => n.showed === true);

  return (
    <div className={classes.chat_wrapper}>
      <div className={classes.message_field_wrapper}>
        <div
          ref={textArea}
          className={`${classes.write_message} ${textArea.current?.innerText
            ? classes.with_value : ''} ${user.nickName ? classes.with_nick_name : ''}`}
          contentEditable
          suppressContentEditableWarning
          onInput={changeMessage}
          onKeyDown={sendMessage}
        />

        <div className={classes.message_field_placeholder}>
          <EditIcon />

          Enter message
        </div>

        {!!searchUser.length && (
          <div className={classes.user_wrapper}>
            {searchUser.map((d) => (
              <div
                key={d.id}
                onClick={() => getUser(d.username, d.id, 'search')}
              >
                {`@${d.username} `}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={classes.chat_main_block} ref={userWrapperRef} onScroll={getUsersScroll}>
        <div className={classes.chat_message_wrapper} ref={chatWrapperRef} onScroll={getChatMsgScroll}>
          {messagesList.map((m, i) => (
            <div className={classes.chat_message} key={`${m.createdAt}_${i}`}>
              <div>
                <p
                  className={m.newMessages ? classes.dark_name : ''}
                  style={+m.userId === +accountHelpers.getUser().id ? { cursor: 'default' } : { cursor: 'pointer' }}
                  onClick={() => getUser(m.senderName, m.userId)}
                >
                  {m.senderName}
                </p>

                <span>
                  {moment.utc(m.createdAt).local().calendar({
                    sameDay: 'LT',
                    nextDay: 'DD.MM  LT',
                    nextWeek: 'DD.MM  LT',
                    lastDay: 'DD.MM  LT',
                    lastWeek: 'DD.MM  LT',
                    sameElse: 'DD.MM  LT',
                  })}
                </span>
              </div>

              <p>{`${m.mention ? `${m.mention} ` : ''}${m.message}`}</p>
            </div>
          ))}

          {chatRequestLoading
              && (
              <div className={classes.chat_loading}>
                <Loader size={24} />
              </div>
              )}
        </div>

        {!!notificationList.length && note && (
        <NavLink
          className={classes.chat_notification_link}
          to={`/scripts/${note.scriptId}?noteId=${note.scriptNote.id}`}
          onClick={() => {}}
        >
          <div className={classes.notification_chat}>
            <div>
              <Bell />
            </div>
            <div className={classes.notification_chat_text}>
              <p>Notification</p>
              <p>
                {note.User.name}
                {' '}
                add notes
                {' '}
                {note.scriptNote.note}
              </p>
            </div>
          </div>
        </NavLink>
        )}

        <div className={classes.tab_content}>
          <p>Users</p>

          <UserTabs
            currentTab={currentTab}
            userRolesTemplate={userRolesTemplate}
            changeTab={changeTab}
          />

          {userLoad
            ? <Loader size={24} className={classes.user_load} />
            : (
              <div className={classes.tab_users_wrapper}>
                {chatUserList.map((d) => (
                  <div

                    onClick={(e) => {
                      e.stopPropagation();
                      getUser(d.username, d.id);
                    }}
                    key={d.id}
                    className={classes.tab_user_wrapper}
                  >
                    <div className={classes.user_image_wrapper}>
                      <img
                        src={d.link}
                        className={classes.user_image}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = avatarImg;
                        }}
                      />

                      <span className={d.online ? classes.active : ''} />
                    </div>

                    <div className={classes.chats_user_name}>
                      <p>{d.username}</p>

                      <p>
                        Role:
                        {d.role}
                      </p>
                    </div>
                  </div>
                ))}

                {userRequestLoading
                && (
                <div className={classes.chat_loading}>
                  <Loader size={24} />
                </div>
                )}
              </div>
            )}
        </div>

      </div>
    </div>
  );
};

export default Chat;
